import { useEffect, useState } from "react";
import { Alert, App, Button, Card, Form, Input, InputNumber, Modal, Select, Switch } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { QuestionDto } from "@audit-plan/shared";
import { api, ApiError } from "../../api/client";
import { useChecklistMutations, useChecklists, useQuestions, type QuestionInput } from "../../api/admin";
import { Pill } from "../pills";
import { palette } from "../../theme";
import { ResponsiveList } from "../ResponsiveList";
import { useModalProps } from "../useModalProps";

const SECS = ["S", "H", "E", "X"];
const secColor: Record<string, string> = { S: palette.secS, H: palette.secH, E: palette.secE, X: palette.secX };

type Pending = { kind: "edit"; q: QuestionDto; body: QuestionInput } | { kind: "new"; body: QuestionInput };

export function ChecklistTab() {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const modalProps = useModalProps(680);
  const cloneModalProps = useModalProps();
  const versions = useChecklists();
  const [vid, setVid] = useState<string>();
  const [sec, setSec] = useState<string>();
  const [edit, setEdit] = useState<{ q?: QuestionDto } | null>(null);
  const [clone, setClone] = useState<{ pending: Pending } | null>(null);
  const [form] = Form.useForm<QuestionInput>();
  const [cform] = Form.useForm<{ code: string; title: string }>();
  const mut = useChecklistMutations();

  useEffect(() => {
    if (!vid && versions.data?.length) setVid((versions.data.find((v) => v.active) ?? versions.data[0])!.id);
  }, [versions.data, vid]);
  const ver = versions.data?.find((v) => v.id === vid);
  const qs = useQuestions(vid);
  const rows = [...(qs.data ?? [])].filter((q) => !sec || q.section === sec).sort((a, b) => SECS.indexOf(a.section) - SECS.indexOf(b.section) || a.order - b.order);

  const open = (q?: QuestionDto) => {
    form.resetFields();
    form.setFieldsValue(q ? { section: q.section, number: q.number, area: q.area, it: q.it, en: q.en, points: q.points, order: q.order, active: q.active, zeroTolerance: q.zeroTolerance } : { section: (sec as QuestionInput["section"]) ?? "S", points: 10, order: (qs.data?.length ?? 0) + 1, active: true, area: "", en: "", zeroTolerance: false });
    setEdit({ q });
  };

  const apply = async (p: Pending, versionId: string) => {
    if (p.kind === "new") await mut.create.mutateAsync({ versionId, body: p.body });
    else await mut.update.mutateAsync({ id: p.q.id, body: p.body });
  };

  const submit = async (body: QuestionInput) => {
    const p: Pending = edit?.q ? { kind: "edit", q: edit.q, body } : { kind: "new", body };
    try {
      await apply(p, vid!);
      void message.success(t("saved"));
      setEdit(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setEdit(null);
        const n = (versions.data?.length ?? 1) + 1;
        cform.setFieldsValue({ code: `rev${n - 1}-${dayjs().format("YYYY-MM-DD")}`, title: `Checklist Supply Chain rev. ${n - 1} del ${dayjs().format("DD/MM/YYYY")}` });
        modal.confirm({
          title: t("a_versionUsed"),
          content: e.message || t("a_versionUsedHelp"),
          okText: t("a_newVersion"),
          cancelText: t("cancel"),
          onOk: () => setClone({ pending: p }),
        });
      } else void message.error(e instanceof ApiError ? e.message : t("a_loadFail"));
    }
  };

  const doClone = async (v: { code: string; title: string }) => {
    if (!clone || !vid) return;
    try {
      const nv = await mut.clone.mutateAsync({ versionId: vid, code: v.code, title: v.title });
      // riapplica la modifica sulla nuova versione
      const p = clone.pending;
      if (p.kind === "edit") {
        const list = await api.get<QuestionDto[] | { items: QuestionDto[] }>(`/checklists/${nv.id}/questions`);
        const target = (Array.isArray(list) ? list : list.items).find((x) => x.code === p.q.code);
        if (target) await mut.update.mutateAsync({ id: target.id, body: p.body });
      } else await mut.create.mutateAsync({ versionId: nv.id, body: p.body });
      setVid(nv.id);
      setClone(null);
      void message.success(t("a_versionCreated"));
    } catch (e) {
      void message.error(e instanceof ApiError ? e.message : t("a_loadFail"));
    }
  };

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12, alignItems: "center" }}>
        <Select style={{ minWidth: "min(320px, 100%)", maxWidth: "100%" }} value={vid} onChange={setVid} loading={versions.isLoading} options={(versions.data ?? []).map((v) => ({ value: v.id, label: `${v.title}${v.active ? ` · ${t("active")}` : ""}` }))} />
        <Select allowClear placeholder={t("section")} style={{ width: 200, maxWidth: "100%" }} value={sec} onChange={setSec} options={SECS.map((s) => ({ value: s, label: `${s} · ${t(`sec_${s}`)}` }))} />
        {ver && <span style={{ fontSize: 12.5, opacity: 0.7 }}>{ver.questionCount} {t("a_questions")} · {ver.usedByAudits} {t("a_usedByAudits")}</span>}
      </div>
      {ver && ver.usedByAudits > 0 && <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t("a_versionUsedHelp")} />}
      <Card
        title={<span>{t("a_questionsTitle")} <span style={{ fontSize: 12, opacity: 0.6 }}>{ver?.code}</span></span>}
        styles={{ body: { padding: 0 } }}
        extra={<Button type="primary" size="small" icon={<PlusOutlined />} disabled={!vid} onClick={() => open()}>{t("newQuestion")}</Button>}
      >
        <ResponsiveList<QuestionDto>
          size="small"
          rowKey="id"
          loading={qs.isLoading}
          dataSource={rows}
          pageSize={30}
          card={{
            code: (q) => `${q.section} ${q.number}${q.area ? ` · ${q.area}` : ""}`,
            title: (q) => q.it,
            tags: (q) => (
              <>
                <Pill tone={q.active ? "ok" : "mute"}>{q.active ? t("active") : t("ans_N")}</Pill>
                {q.section !== "X" && <Pill tone="info">{`${q.points} ${t("points")}`}</Pill>}
                {q.zeroTolerance && <Pill tone="bad">{t("d_zeroTolShort")}</Pill>}
              </>
            ),
            footer: (q) => (
              <Button size="small" onClick={() => open(q)}>
                {t("edit")}
              </Button>
            ),
          }}
          columns={[
            { title: t("section"), dataIndex: "section", render: (v: string) => <span><i style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: secColor[v], marginRight: 6 }} />{v}</span> },
            { title: t("number"), dataIndex: "number", render: (v: string) => <span style={{ fontFamily: "var(--ap-mono)" }}>{v}</span> },
            { title: t("area"), dataIndex: "area", render: (v: string) => <span style={{ fontSize: 12 }}>{v}</span> },
            { title: t("textIt"), dataIndex: "it" },
            { title: t("points"), dataIndex: "points", align: "right" as const, render: (v: number, q) => (q.section === "X" ? "—" : v) },
            { title: t("active"), dataIndex: "active", render: (v: boolean) => <Pill tone={v ? "ok" : "mute"}>{v ? t("ans_Y") : t("ans_N")}</Pill> },
            { title: t("d_zeroTol"), dataIndex: "zeroTolerance", render: (v: boolean, q) => (q.section === "X" ? "—" : v ? <Pill tone="bad">{t("d_zeroTolShort")}</Pill> : "") },
            { title: "", render: (_, q) => <Button size="small" type="text" onClick={() => open(q)}>{t("edit")}</Button> },
          ]}
        />
      </Card>

      <Modal open={!!edit} onCancel={() => setEdit(null)} onOk={() => form.submit()} okText={t("save")} cancelText={t("cancel")} confirmLoading={mut.create.isPending || mut.update.isPending} {...modalProps} title={edit?.q ? `${t("edit")} ${edit.q.code}` : t("newQuestion")} destroyOnHidden>
        <Form form={form} layout="vertical" onFinish={submit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 16 }}>
            <Form.Item name="section" label={t("section")}><Select options={SECS.map((s) => ({ value: s, label: `${s} · ${t(`sec_${s}`)}` }))} /></Form.Item>
            <Form.Item name="number" label={t("number")} rules={[{ required: true, message: t("required") }]}><Input placeholder="1.35" /></Form.Item>
          </div>
          <Form.Item name="area" label={t("area")}><Input /></Form.Item>
          <Form.Item name="it" label={t("textIt")} rules={[{ required: true, message: t("required") }]}><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="en" label={t("textEn")}><Input.TextArea rows={3} /></Form.Item>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", columnGap: 16 }}>
            <Form.Item name="points" label={t("points")}><InputNumber min={0} max={50} style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="order" label={t("a_order")}><InputNumber style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="active" label={t("active")} valuePropName="checked"><Switch /></Form.Item>
          </div>
          <Form.Item name="zeroTolerance" label={t("d_zeroTol")} valuePropName="checked" extra={t("d_zeroTolHint")}><Switch /></Form.Item>
        </Form>
      </Modal>

      <Modal open={!!clone} onCancel={() => setClone(null)} onOk={() => cform.submit()} okText={t("a_newVersion")} cancelText={t("cancel")} confirmLoading={mut.clone.isPending} title={t("a_newVersion")} {...cloneModalProps} destroyOnHidden>
        <Form form={cform} layout="vertical" onFinish={doClone}>
          <Form.Item name="code" label={t("code")} rules={[{ required: true, message: t("required") }]}><Input /></Form.Item>
          <Form.Item name="title" label={t("title")} rules={[{ required: true, message: t("required") }]}><Input /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
